import { API_PATHS } from '@lunya/contracts'

import { tokenStorage } from '~/shared/api/http/tokenStorage'
import { API_BASE_URL, IS_DEV, USE_MOCKS } from '~/shared/config'

import type { AnyAnalyticsEvent } from './events'

type QueuedEvent = { name: string; params?: Record<string, unknown>; client_at: string }

/**
 * Отправка событий на бэкенд.
 *
 * ДО ЭТОГО ФАЙЛА АНАЛИТИКА НЕ ДОЕЗЖАЛА НИКУДА. В приложении расставлено больше семидесяти
 * событий, а транспорт складывал их в массив в памяти на 200 элементов и забывал: ни ручки,
 * ни провайдера, ни таблицы. Инструментирование было, труб к нему не было, и все замеры
 * воронки приходилось делать по косвенным следам в базе.
 *
 * ПОЧЕМУ ПАЧКАМИ. Мини-аппу сворачивают и открывают постоянно, а запрос на каждое нажатие —
 * это десятки запросов за сессию на мобильной сети. Копим и отправляем раз в несколько секунд
 * или когда накопилось достаточно.
 *
 * ПОЧЕМУ `sendBeacon` НА ЗАКРЫТИИ. Обычный `fetch` при уходе со страницы браузер отменяет,
 * и терялась бы ровно последняя пачка — та, в которой лежит причина ухода. `sendBeacon`
 * переживает закрытие вкладки и сворачивание мини-аппы.
 *
 * ПОЧЕМУ ОШИБКИ ГЛОТАЮТСЯ. Аналитика не имеет права ломать приложение или показывать человеку
 * что-либо. Не доехало — потеряли событие, и это дешевле любой другой реакции.
 */

/** Столько событий копим до отправки. Больше сотни ручка не примет (контракт). */
const BATCH_SIZE = 20

/** Как часто сбрасываем накопленное, даже если пачка не набралась. */
const FLUSH_INTERVAL_MS = 5000

/** Потолок очереди на случай, когда сеть недоступна долго. Роняем самые старые. */
const MAX_QUEUE_SIZE = 200

const ENDPOINT = `${API_BASE_URL}${API_PATHS.analytics.events}`

const queue: QueuedEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null

function logToConsole(event: AnyAnalyticsEvent): void {
  // eslint-disable-next-line no-console
  console.groupCollapsed(`%c[analytics] ${event.name}`, 'color:#8b5cf6;font-weight:600')
  // eslint-disable-next-line no-console
  console.log(event.params)
  // eslint-disable-next-line no-console
  console.groupEnd()
}

/** Отправка через `fetch`. Токен кладём, если он есть: события до авторизации ручка тоже примет. */
async function send(events: readonly QueuedEvent[]): Promise<void> {
  const token = tokenStorage.getAccessToken()

  await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ events }),
    keepalive: true,
  })
}

/**
 * Отправка на выходе. `sendBeacon` не умеет заголовков, поэтому токена тут нет — событие
 * запишется обезличенным. Потерять привязку к человеку в последней пачке лучше, чем потерять
 * саму пачку целиком.
 */
function sendOnExit(events: readonly QueuedEvent[]): void {
  const body = new Blob([JSON.stringify({ events })], { type: 'application/json' })
  navigator.sendBeacon(ENDPOINT, body)
}

function flush(isExit = false): void {
  if (queue.length === 0) return

  const batch = queue.splice(0, queue.length)

  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }

  if (isExit) {
    sendOnExit(batch)
    return
  }

  void send(batch).catch(() => {
    // Молча: аналитика не имеет права ни ломать приложение, ни показывать человеку ошибку.
  })
}

function scheduleFlush(): void {
  if (timer !== null) return
  timer = setTimeout(() => flush(), FLUSH_INTERVAL_MS)
}

function enqueue(event: AnyAnalyticsEvent): void {
  queue.push({
    name: event.name,
    ...(event.params ? { params: event.params as Record<string, unknown> } : {}),
    client_at: new Date().toISOString(),
  })

  if (queue.length > MAX_QUEUE_SIZE) queue.shift()
  if (queue.length >= BATCH_SIZE) {
    flush()
    return
  }

  scheduleFlush()
}

/**
 * Событие. В разработке — читаемый вывод в консоль, в проде — очередь с отправкой пачками.
 * С моками ничего не отправляем: локальные клики не должны попадать в боевую статистику.
 */
function track(event: AnyAnalyticsEvent): void {
  if (IS_DEV || USE_MOCKS) {
    logToConsole(event)
    return
  }

  enqueue(event)
}

/**
 * Подписка на уход со страницы.
 *
 * `visibilitychange` вместо `beforeunload`: в мобильных браузерах и внутри Telegram последний
 * не срабатывает при сворачивании, а именно так мини-аппу и закрывают чаще всего.
 */
function install(): void {
  if (typeof document === 'undefined') return

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true)
  })
}

if (!IS_DEV && !USE_MOCKS) install()

/** @internal Только для отладки и тестов — очередь ещё не отправленных событий. */
function getQueuedEvents(): readonly QueuedEvent[] {
  return queue
}

export const analytics = {
  track,
  flush,
  getQueuedEvents,
}
